import {
  JSXElementConstructor,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { DesignSystemProvider } from "chameleon/lib/components";

import {
  iconClass,
  pointer,
  tooltipThemeWrapper,
  tooltipWrapperClass,
} from "./tooltip.css";

export interface TooltipProps {
  /**
   * Children that display and cause the hover
   */
  children: ReactElement<$TSNoType, string | JSXElementConstructor<$TSNoType>>;
  /**
   * Content to show in tooltip
   */
  content: ReactElement<$TSNoType, string | JSXElementConstructor<$TSNoType>>;
  /**
   * Use the standard tooltip template or have no styling
   */
  useTemplate?: boolean;
  /**
   * Theme for color palette
   */
  theme?: "dark" | "light";
  /**
   * Position of tooltip
   */
  tooltipPosition?: "top" | "right" | "bottom" | "left";
  /**
   * Aria label of tooltip
   */
  ariaLabel?: string;
}

type PositionStyle = {
  top: string;
  left: string;
};

export const Tooltip = ({
  children,
  content,
  useTemplate = true,
  tooltipPosition = "left",
  ariaLabel = "Tooltip",
}: TooltipProps) => {
  const tooltipContainer =
    document.getElementById("tooltip-container") || document.body;

  const [isHovering, setIsHovering] = useState<boolean>(false);
  const tooltipElementRef = useRef<HTMLDivElement>(null);
  const tooltipContainerRef = useRef<HTMLButtonElement>(null);
  const [tooltipPositionStyle, setTooltipPositionStyle] =
    useState<PositionStyle>({} as PositionStyle);
  const [pointerPositionStyle, setPointerPositionStyle] = useState({});
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleKeyDown = (event: { key: string }) => {
    if (document.activeElement === tooltipContainerRef.current) {
      if (event.key === "Escape") setIsHovering(false);
      else if (event.key === "Enter") setIsHovering(true);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    const clickedOutsideTooltip =
      tooltipElementRef.current &&
      !tooltipElementRef.current.contains(event.target as Node);
    const clickedOutsideContainer =
      tooltipContainerRef.current &&
      !tooltipContainerRef.current.contains(event.target as Node);

    if (clickedOutsideTooltip && clickedOutsideContainer) {
      setIsHovering(false);
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!isHovering) return;

    const tooltipDimensions =
      tooltipElementRef?.current?.getBoundingClientRect();
    const baseElementDimensions =
      tooltipContainerRef?.current?.children[0].getBoundingClientRect();

    if (!baseElementDimensions || !tooltipDimensions) return;

    const scrollPosition = window.scrollY;
    const pageOverflowCalc =
      window.innerWidth -
      (baseElementDimensions.left +
        tooltipDimensions.width / 2 +
        baseElementDimensions.width / 2);
    const overflowAdjustment = pageOverflowCalc < 0 ? pageOverflowCalc : 0;

    // Common calculations used across different positions
    const horizontalCenter =
      baseElementDimensions.left + baseElementDimensions.width / 2;
    const verticalCenter =
      scrollPosition +
      baseElementDimensions.top +
      baseElementDimensions.height / 2;

    switch (tooltipPosition) {
      case "top":
        setTooltipPositionStyle({
          top: `${
            scrollPosition +
            baseElementDimensions.top -
            baseElementDimensions.height -
            tooltipDimensions.height / 2
          }px`,
          left: `${horizontalCenter + overflowAdjustment}px`,
        });

        setPointerPositionStyle({
          left: `calc(50% - ${overflowAdjustment}px)`,
          top: `100%`,
        });
        break;

      case "bottom":
        setTooltipPositionStyle({
          top: `${
            scrollPosition +
            baseElementDimensions.bottom +
            tooltipDimensions.height / 2 +
            20
          }px`,
          left: `${horizontalCenter + overflowAdjustment}px`,
        });

        setPointerPositionStyle({
          left: `calc(50% - ${overflowAdjustment}px)`,
          top: `0px`,
        });
        break;

      case "right":
        setTooltipPositionStyle({
          top: `${verticalCenter}px`,
          left: `${
            baseElementDimensions.left +
            baseElementDimensions.width +
            tooltipDimensions.width / 2
          }px`,
        });

        setPointerPositionStyle({
          left: `0px`,
          top: `50%`,
        });
        break;

      default: // "left" is the default
        setTooltipPositionStyle({
          top: `${verticalCenter}px`,
          left: `${baseElementDimensions.left - tooltipDimensions.width / 2 - 20}px`,
        });

        setPointerPositionStyle({
          left: `100%`,
          top: `50%`,
        });
    }
  }, [isHovering, tooltipPosition]);

  const clearHoverTimeout = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  };

  const handleMouseEnter = () => {
    clearHoverTimeout();
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => setIsHovering(false), 600);
  };

  const handleFocus = handleMouseEnter;
  const handleBlur = () => {
    clearHoverTimeout();
    setIsHovering(false);
  };

  return (
    <button
      ref={tooltipContainerRef}
      data-testid="tooltipContainer"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      className={iconClass}
      aria-label={ariaLabel}
      {...(isHovering ? { "aria-describedby": "tooltipId" } : {})}
    >
      {children}
      <>
        {isHovering &&
          createPortal(
            <DesignSystemProvider>
              <div
                ref={tooltipElementRef}
                id="tooltipId"
                role="tooltip"
                className={[
                  tooltipWrapperClass[tooltipPosition],
                  useTemplate ? tooltipThemeWrapper : "",
                ].join(" ")}
                style={{ ...tooltipPositionStyle, zIndex: 10000 }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <div className={pointer} style={pointerPositionStyle}></div>
                {content}
              </div>
            </DesignSystemProvider>,
            tooltipContainer
          )}
      </>
    </button>
  );
};
